import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Check, X, Music2, Layers, BarChart3 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useUpload } from '../contexts/UploadContext';
import { trackEvent } from '../services/analytics';
import { submitEmailReminder } from '../services/api';

// ─── Post-upload anon-uploader capture ───────────────────────────────────
//
// Renders for anonymous users after at least one file upload has completed.
// One card, two columns on desktop: pitch on the left (loud headline +
// sub + CTA + quiet supports), personal /my-files-shaped peek on the
// right (top row = user's just-uploaded file marked "yours" + two dummy
// siblings). Stacks on mobile. Email fallback (Zone 2) below.
//
// Design history:
//   b9617d6 (2026-07-05) added an "Inside pastewaves" tile strip above
//     Zone 1: three aspect-square illustrations of /my-files, collections,
//     /stats. The strip pitched what pastewaves DOES while the CTA below
//     asked for sign-in-to-keep-THIS-file — two different pitches stacked,
//     three equal-weight tiles killing Von Restorff. Glenn flagged messy.
//   e6aa322 (2026-07-09) killed the whole tile strip after Donald's
//     brief — through-line was "keep THIS file," and the illustrated
//     grid built toward the wrong ask. Landed a two-column shape with
//     quiet supporting benefits under the CTA and no visual peek.
//   THIS commit — brought back a peek surface in a fundamentally
//     different shape. NOT three equal illustrated tiles; ONE compact
//     tile personalized to the user's own file. The top row shows their
//     actual filename with a soundwave-highlighted "yours" indicator;
//     two dummy siblings underneath establish the /my-files list shape.
//     Ties the visual directly to "keep THIS file" rather than pitching
//     the whole product. Glenn's call: option 1 in the peek-inside
//     retro after e6aa322 shipped. "peek inside kind of."
//
//   Zone 1 (primary, always rendered):
//     Loud headline "Keep your {file(s)}." + sub-copy naming the specific
//     "another 14 days from today" outcome. Accurate per Gilfoyle's
//     verification of handleClaimFile at lambda-file-metadata/main.go:2886
//     — TTL resets to now + 14d on claim, not extends-in-place. Google
//     sign-in button below the sub. Three quiet single-line supporting
//     benefits under the CTA (Music2 / Layers / BarChart3 + one-liner each).
//     Right column on desktop: personal peek tile with the user's file
//     as the top row (soundwave-tinted, "yours" label) plus two dummy
//     siblings. Stacks below the pitch on mobile.
//
//   Zone 2 (secondary, feature-flag gated):
//     Leave an email → 48h pre-expiry reminder → link back into the same
//     sign-in-and-claim flow. Lighter-touch capture for readers who won't
//     sign up in-session. Gated behind REACT_APP_EMAIL_REMINDER_URL so
//     the entire zone stays out of the DOM until Gilfoyle ships the
//     backend Lambda + DDB row + SES cron. The api.ts stub throws until
//     then. Zone 2 is UNCHANGED across this iteration.
//
// Voice constraints locked with Glenn 2026-07-02 (still apply):
//   - No exclamation marks, no sparkle icons, no "Free" pill spam.
//   - No infinite pulse/rotate animations. Motion is enter/exit only.
//   - No engineer-nouns ("Usage Analytics" / "Storage Stats") — the anon
//     first-timer has no dashboard-need to shout about.
//   - Zone 2 promise is a hard commitment: ONE reminder, and this email
//     list is never reused for product updates.
//
// Preserved from prior iterations:
//   - Session-storage dismiss with re-open on new upload completion.
//   - `claim_prompt_shown` telemetry with `allowAnon: true` (Donald's L2
//     funnel numerator, fixed 2026-07-05 in a2a10b7 — load-bearing, do
//     not remove the allowAnon flag).
//   - `claimSigninClicked` localStorage buffer flushed by AuthContext
//     post-login (upper-funnel instrumentation).
//   - Poll fallback so uploads that complete outside completedFiles-context
//     updates still surface the card within ~2s.
//   - Render gate: !isAuthenticated && !isDismissed && pendingClaims.length > 0.
//     UNCHANGED across this iteration — beacon fires on the same trigger.
// ─────────────────────────────────────────────────────────────────────────

interface PendingClaim {
  fileId: string;
  claimToken: string;
  fileName: string;
  timestamp: number;
}

// URL-presence gate: the whole Zone 2 email-capture affordance renders only
// when REACT_APP_EMAIL_REMINDER_URL is set. One env var, no drift between a
// "feature enabled" boolean and an "endpoint URL" — either the backend is
// wired or it isn't. Gilfoyle dark-launches by omitting the env var until
// the Lambda + DDB + SES cron are green.
const EMAIL_REMINDER_URL = process.env.REACT_APP_EMAIL_REMINDER_URL;

// RFC-lite email shape check — strict enough to catch typos ("user@gmailcom"),
// loose enough not to reject legit ccTLD or plus-address forms.
const looksLikeEmail = (v: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

export const SignInToClaimBanner: React.FC = () => {
  const { isAuthenticated, signIn, getValidToken } = useAuth();
  const { completedFiles } = useUpload();
  const [pendingClaims, setPendingClaims] = useState<PendingClaim[]>([]);
  const [isDismissed, setIsDismissed] = useState(() => {
    return sessionStorage.getItem('claimBannerDismissed') === 'true';
  });

  // Email-capture state (only meaningful when EMAIL_REMINDER_ENABLED).
  const [email, setEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState<'idle' | 'submitting' | 'done' | 'error'>('idle');
  const [emailError, setEmailError] = useState<string | null>(null);

  // Guard so the visibility-tracking event fires at most once per mount,
  // even though `loadPendingClaims` polls every 2s.
  const claimPromptTrackedRef = useRef<boolean>(false);

  const loadPendingClaims = useCallback(() => {
    if (isAuthenticated || isDismissed) {
      setPendingClaims([]);
      return;
    }

    try {
      const storedClaims = localStorage.getItem('pendingClaims');
      if (storedClaims) {
        const claims: PendingClaim[] = JSON.parse(storedClaims);
        const validClaims = claims.filter(
          claim => Date.now() - claim.timestamp < 24 * 60 * 60 * 1000
        );
        if (validClaims.length !== claims.length) {
          localStorage.setItem('pendingClaims', JSON.stringify(validClaims));
        }
        setPendingClaims(validClaims);
      } else {
        setPendingClaims([]);
      }
    } catch (error) {
      console.error('Failed to load pending claims:', error);
    }
  }, [isAuthenticated, isDismissed]);

  useEffect(() => {
    loadPendingClaims();
  }, [loadPendingClaims]);

  useEffect(() => {
    if (isAuthenticated) return;

    if (completedFiles > 0) {
      // Un-dismiss when new uploads complete — a fresh drop earns another
      // prompt even if the user dismissed a previous instance in the same
      // session.
      if (sessionStorage.getItem('claimBannerDismissed') === 'true') {
        sessionStorage.removeItem('claimBannerDismissed');
        setIsDismissed(false);
      }
      const timer = setTimeout(() => loadPendingClaims(), 100);
      return () => clearTimeout(timer);
    }

    const pollInterval = setInterval(() => loadPendingClaims(), 2000);
    return () => clearInterval(pollInterval);
  }, [completedFiles, isAuthenticated, loadPendingClaims]);

  const handleDismiss = () => {
    setIsDismissed(true);
    sessionStorage.setItem('claimBannerDismissed', 'true');
  };

  const handleSignIn = () => {
    try {
      localStorage.setItem('claimSigninClicked', String(Date.now()));
    } catch {
      // localStorage can throw in private mode; never break sign-in.
    }
    signIn();
  };

  const handleEmailSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (emailStatus === 'submitting' || emailStatus === 'done') return;
    if (!looksLikeEmail(email)) {
      setEmailError('That doesn’t look like an email.');
      return;
    }
    setEmailStatus('submitting');
    setEmailError(null);
    try {
      const fileIds = pendingClaims.map(c => c.fileId);
      await submitEmailReminder(email.trim(), fileIds);
      setEmailStatus('done');
      // Funnel signal — Donald's Zone-2 numerator. Uploader is anonymous
      // (that's the whole capture premise), so allowAnon is required. The
      // backend allowlist in lambda-events/main.go must accept this event
      // AND permit an anon caller; without both, the beacon is silently
      // dropped at the API edge.
      trackEvent('email_reminder_captured', getValidToken, { allowAnon: true });
    } catch (err) {
      console.error('email reminder submit failed:', err);
      setEmailStatus('error');
      setEmailError(err instanceof Error ? err.message : 'Something went wrong. Try again in a moment.');
    }
  };

  // Fire `claim_prompt_shown` exactly once per mount, the first time the
  // visibility gate would pass. Mirrors the render gate so the event
  // tracks the actual "banner became visible" moment.
  // allowAnon: true is load-bearing — this banner ONLY renders for anon
  // users (that's the whole premise), so without allowAnon the beacon
  // gets rejected at the API edge for lack of auth. Prior code omitted
  // the flag and silently dropped every visibility event; 30d
  // CloudWatch shows 0 across all users despite N=38 clicks + N=48
  // claim completions. Fixed 2026-07-05.
  useEffect(() => {
    if (claimPromptTrackedRef.current) return;
    if (isAuthenticated || isDismissed) return;
    if (pendingClaims.length === 0) return;
    if (sessionStorage.getItem('claimBannerDismissed') === 'true') return;
    claimPromptTrackedRef.current = true;
    trackEvent('claim_prompt_shown', getValidToken, { allowAnon: true });
  }, [isAuthenticated, isDismissed, pendingClaims, getValidToken]);

  if (
    isAuthenticated ||
    isDismissed ||
    pendingClaims.length === 0 ||
    sessionStorage.getItem('claimBannerDismissed') === 'true'
  ) {
    return null;
  }

  const fileCount = pendingClaims.length;
  const fileWord = fileCount === 1 ? 'file' : 'files';
  const themWord = fileCount === 1 ? 'it' : 'them';

  return (
    <motion.div
      className="relative overflow-hidden rounded-2xl bg-white/[0.02] border border-white/8 px-5 py-5 md:px-6 md:py-6"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
    >
      {/* Dismiss X — top-right, quiet. Smaller than the pitch title so it
          doesn't compete for attention. Escape hatch, not co-equal chrome. */}
      <button
        onClick={handleDismiss}
        aria-label="Dismiss"
        className="absolute top-3 right-3 p-1 rounded-lg text-white/25 hover:text-white/60 hover:bg-white/5 transition-colors"
      >
        <X className="w-3.5 h-3.5" />
      </button>

      {/* ─── Zone 1 — Sign in and keep it, with a personal peek ─────────── */}
      <div className="pr-8">
        <div className="md:grid md:grid-cols-[minmax(0,1fr)_14rem] md:gap-6 md:items-start">
          {/* Pitch column — headline, sub, CTA, supports */}
          <div>
            <h3 className="text-xl md:text-2xl font-semibold text-white leading-tight tracking-tight">
              Keep your {fileWord}.
            </h3>
            <p className="mt-2 text-sm text-white/60 leading-relaxed max-w-prose">
              Signing in claims your {fileWord} and keeps {themWord}{' '}
              <span className="text-white/80 font-medium">another 14 days from today</span>.
            </p>
            <button
              onClick={handleSignIn}
              className="mt-4 inline-flex items-center px-4 py-2.5 rounded-xl text-sm font-medium text-white bg-soundwave-500 hover:bg-soundwave-600 transition-colors"
            >
              Sign in with Google
            </button>

            {/* Quiet supporting benefits — one-liners, not illustrations. Same
                three value props as the old tile strip, expressed as supports
                rather than co-equal art. Von Restorff: the headline is the one
                distinct thing; these are the quiet reasons. */}
            <ul className="mt-5 flex flex-col gap-2 text-xs md:text-sm text-white/45">
              <li className="flex items-center gap-2.5">
                <Music2 className="w-3.5 h-3.5 text-white/35 flex-shrink-0" />
                <span>Organize with artwork, tags, and search.</span>
              </li>
              <li className="flex items-center gap-2.5">
                <Layers className="w-3.5 h-3.5 text-white/35 flex-shrink-0" />
                <span>Group tracks into collections.</span>
              </li>
              <li className="flex items-center gap-2.5">
                <BarChart3 className="w-3.5 h-3.5 text-white/35 flex-shrink-0" />
                <span>See how they’re played.</span>
              </li>
            </ul>
          </div>

          {/* Peek column — /my-files-shaped mini tile with the user's just-
              uploaded file as the top row (soundwave-highlighted, "yours"
              label) plus two dummy siblings. Personal preview of what signing
              in looks like, tied to THIS file. Replaces the illustrated tile
              strip from b9617d6 with content that carries the "keep THIS file"
              through-line into visual form rather than pitching generic
              product surfaces. Filename comes from pendingClaims[0].fileName
              (original upload name, extension stripped). */}
          <div className="mt-6 md:mt-0">
            <div className="text-[10px] uppercase tracking-[0.15em] text-white/35 font-medium mb-2">
              Peek inside
            </div>
            <div className="rounded-xl bg-black/30 border border-white/8 overflow-hidden">
              <div className="px-3 pt-2.5 pb-1.5 flex items-center gap-1.5">
                <Music2 className="w-3 h-3 text-white/40 flex-shrink-0" />
                <div className="text-[9px] uppercase tracking-wider text-white/50 font-medium">
                  My files
                </div>
              </div>
              <div className="flex flex-col gap-0.5 px-1.5 pb-1.5">
                {/* User's row — soundwave-highlighted, "yours" label */}
                <div className="flex items-center gap-2 px-1.5 py-1.5 rounded-md bg-soundwave-500/12 ring-1 ring-soundwave-500/25">
                  <div className="w-7 h-7 rounded-md bg-gradient-to-br from-soundwave-500/60 to-soundwave-700/60 flex items-center justify-center flex-shrink-0">
                    <Music2 className="w-3 h-3 text-white/90" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] text-white/95 font-medium truncate">
                      {(pendingClaims[0].fileName.replace(/\.[^.]+$/, '') || 'Your track')}
                    </div>
                    <div className="text-[9px] text-soundwave-300/90 uppercase tracking-[0.1em] font-medium">
                      yours
                    </div>
                  </div>
                </div>
                {/* Dummy sibling — Bassline sketch */}
                <div className="flex items-center gap-2 px-1.5 py-1.5 rounded-md">
                  <div className="w-7 h-7 rounded-md bg-gradient-to-br from-amber-400 to-orange-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] text-white/55 truncate">Bassline sketch</div>
                  </div>
                  <div className="text-[9px] text-white/30 tabular-nums flex-shrink-0">3:12</div>
                </div>
                {/* Dummy sibling — Bridge idea v2 */}
                <div className="flex items-center gap-2 px-1.5 py-1.5 rounded-md">
                  <div className="w-7 h-7 rounded-md bg-gradient-to-br from-emerald-500 to-teal-700 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] text-white/55 truncate">Bridge idea v2</div>
                  </div>
                  <div className="text-[9px] text-white/30 tabular-nums flex-shrink-0">1:47</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Zone 2 — Or leave an email (URL-presence gated) ────────────── */}
      {EMAIL_REMINDER_URL && (
        <div className="mt-5 pt-5 border-t border-white/8">
          <div className="text-xs text-white/50 tracking-wide uppercase font-medium">
            Not signing in?
          </div>

          {emailStatus === 'done' ? (
            <div className="mt-2 flex items-center gap-2 text-sm text-white/70">
              <Check className="w-4 h-4 text-soundwave-400 flex-shrink-0" />
              <span>Got it. We’ll email you before your {fileWord} expire.</span>
            </div>
          ) : (
            <>
              <p className="mt-1 text-sm text-white/70 leading-relaxed max-w-prose">
                Leave an email. We’ll send{' '}
                <span className="text-white font-medium">one</span> reminder 48 hours
                before your {fileWord} would expire — and that’s the only email you’ll
                get from us.
              </p>
              <form onSubmit={handleEmailSubmit} className="mt-3 flex items-stretch gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) setEmailError(null);
                  }}
                  placeholder="you@example.com"
                  disabled={emailStatus === 'submitting'}
                  aria-label="Your email"
                  className="flex-1 min-w-0 h-10 px-3 rounded-xl bg-white/[0.03] border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-soundwave-500/50 focus:bg-white/[0.05] transition-colors disabled:opacity-60"
                />
                <button
                  type="submit"
                  disabled={emailStatus === 'submitting' || !email.trim()}
                  className="h-10 px-4 rounded-xl text-sm font-medium text-white/80 bg-white/[0.05] hover:bg-white/[0.10] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {emailStatus === 'submitting' ? 'Sending…' : 'Remind me'}
                </button>
              </form>
              {emailError && (
                <div className="mt-2 text-xs text-red-300/80">{emailError}</div>
              )}
            </>
          )}
        </div>
      )}
    </motion.div>
  );
};
